// canApp — per-team app-access CRUD gate. Layered ON TOP of feature
// flags (featureFlags.js, same folder — a product on/off switch): this
// is an admin-configurable ACL, gating whether a signed-in member's
// TEAM grants a given `app.<key>.<verb>` permission. Off by default —
// every workspace ships with `workspace.settings.appAccessControl`
// unset, so canApp is a no-op bypass until an admin opts in from the
// Apps matrix on /admin/teams (workspace/pages/admin/teams.js
// _enforceRow, writes via workspace/functions/featureFlags.js
// setAppAccessEnforced). The 12-app × 4-verb `app.<key>.<verb>`
// vocabulary itself lives in workspace/functions/permissions.js
// (APP_ACCESS_APPS / APP_ACCESS_VERBS, mirroring
// server/src/core/constants/permissions.js @554832fb) — this file only
// builds/reads the permission strings, it does not own the catalog.
//
// Lives in SHARED (not workspace) because navItemVisible (featureFlags.js,
// same folder) needs it, and shared is consumed by every host (workspace,
// canvas, marketplace, preview) — it must not depend on the workspace
// package. That's also why the owner/admin bypass below REPLICATES the
// claim-based legs of workspace/functions/roleHelpers.js `hasPermission`
// (owner bypass) and `currentUserIsAdmin` (owner/admin claim signals,
// roleHelpers.js:26-99) instead of importing them — same primitives
// (root.jwtClaims.is_owner / org_role / workspace_role / symbols_role,
// root.org.role), read directly off `root` so this file stays free of
// cross-package imports. The one leg intentionally NOT replicated is
// currentUserIsAdmin's legacy `root.people` / `roles.byUser` fallback —
// that needs `findPerson` from workspace-shared/functions/peopleUtils.js
// and is a dual-read-migration leg for pre-JWT-permission data; every
// account new enough to have `appAccessControl` also has claim-based
// owner/admin signals, so skipping it costs nothing here.
import { stripWorkspacePrefix } from './workspacePrefix.js'
import { workspaceSettings } from './workspaceSettings.js'

// Exported (not just the local `canApp` leg below) so other shared files
// that need "can this viewer act on an admin-only gap" get ONE role check
// to call instead of writing a fourth copy — el.call('isOwnerOrAdmin', root)
// or isOwnerOrAdmin(root) directly. First reuse: VoiceControls.js /
// voiceSession.js role-aware "no agent configured" notice (VOICE-CONVAI
// engineering half, 2026-08-11) — same claim primitives, still no
// cross-package import.
//
// FA204 / frank trap — _isOwnerLikeRole moved from module scope into this
// function (sole consumer, pure helper — see longPress.js for the
// identical shape).
export const isOwnerOrAdmin = (root) => {
  const isOwnerLikeRole = (r) => r === 'owner' || r === 'admin'
  const claims = root?.jwtClaims || {}
  if (claims.is_owner === true) return true
  if (isOwnerLikeRole(claims.org_role)) return true
  if (isOwnerLikeRole(claims.workspace_role)) return true
  if (claims.symbols_role === 'admin' || claims.symbolsRole === 'admin') return true
  if (isOwnerLikeRole(root?.org?.role)) return true
  return false
}

// Permission-array resolution — mirrors the PREFERRED branches of
// workspace/functions/claimResolver.js `resolveClaims` (server-enriched
// `org.permissions` / `org.intranetPermissions` win over the base JWT
// claim). The one branch skipped is claimResolver's local team-perms
// UNION fallback (for when the server hasn't enriched `org.permissions`
// yet) — that needs workspace's PERMISSION_KEYS / CATEGORY_PERMISSION_
// TEMPLATES catalog, a workspace-only import this file can't take. In
// practice `org.permissions` is populated by the same `/auth/me` call
// every claims-reading gate already depends on, so the fallback gap is
// the same cold-load window every other claim gate tolerates.
// el.call('canApp', appKey, verb) or canApp(root, appKey, verb). Verb
// defaults to 'read' — the level every nav-gating call site needs.
//
// Team.settings.apps[appKey] is the reserved per-app-config extension
// point (e.g. per-app defaults/overrides beyond the CRUD verbs below) —
// no reader exists yet; noted here so the next per-app-config feature
// doesn't have to rediscover the convention.
//
// FA204 / frank trap — self-contained inline copies of
// isOwnerOrAdmin/_permissionsFromRoot (a bare sibling-export/module-local
// read is exactly as unresolvable post-frank as either alone); the
// isOwnerOrAdmin export above stays live for its genuine external
// consumers (VoiceControls.js, voiceSession.js).
export const canApp = (root, appKey, verb = 'read') => {
  // DECISION site (spec §5) — deliberately does NOT gate on `ready`, and the
  // reason has to be written down because the default here is PERMISSIVE.
  // "enforcement is off" and "we don't know yet whether enforcement is on"
  // both resolve to `true` (allow). That is the correct direction: this gate
  // hides navigation, so failing closed would blank a member's whole app list
  // for the pre-arrival window and then pop it back in — strictly worse than
  // briefly showing an app that a later tick removes, and it can never leak
  // DATA (the server enforces access independently; this only decides what is
  // offered in the UI).
  const isOwnerLikeRole = (r) => r === 'owner' || r === 'admin'
  const ownerOrAdmin = (r) => {
    const claims = r?.jwtClaims || {}
    if (claims.is_owner === true) return true
    if (isOwnerLikeRole(claims.org_role)) return true
    if (isOwnerLikeRole(claims.workspace_role)) return true
    if (claims.symbols_role === 'admin' || claims.symbolsRole === 'admin') return true
    if (isOwnerLikeRole(r?.org?.role)) return true
    return false
  }
  const permissionsFromRoot = (r) => {
    const org = r?.org
    if (org && Array.isArray(org.permissions)) return org.permissions
    if (org && Array.isArray(org.intranetPermissions)) return org.intranetPermissions
    return r?.jwtClaims?.permissions || []
  }

  const { settings } = workspaceSettings(root)
  if (!settings?.appAccessControl?.enforced) return true
  if (ownerOrAdmin(root)) return true
  return permissionsFromRoot(root).includes(`app.${appKey}.${verb}`)
}

// appKeyForRoute(path) — null for anything outside the 12-app
// vocabulary (studio / infra / agents / marketplace / team-members / …),
// so navItemVisible's canApp hook leaves those items visible.
//
// FA204 / frank trap — APP_ROUTES moved from module scope into this
// function (sole consumer, pure literal — see longPress.js for the
// identical shape).
export const appKeyForRoute = (path) => {
  // Route prefix → app-access key. Deliberately independent from
  // featureFlags.js's ROUTE_FLAGS (route → FEATURE flag key): the two
  // vocabularies mostly overlap by naming coincidence (calendar / chat /
  // meet / documents / files / analytics / data / announcements / company
  // all share their route prefix as both a flag key AND an app-access
  // key) but diverge on Tickets (feature-flag key `tasks`, app-access key
  // `tickets`) and Reminders (no ROUTE_FLAGS entry yet). Keeping this
  // table independent also means it never collides with concurrent edits
  // to ROUTE_FLAGS.
  //
  // '/reminders' is a best-effort route guess — APP_ACCESS_APPS already
  // carries a `reminders` app key (server@554832fb) ahead of its own
  // top-level route/page landing; update this entry if that route ships
  // under a different path.
  const APP_ROUTES = [
    ['/calendar', 'calendar'],
    ['/chat', 'chat'],
    ['/meet', 'meet'],
    // /notes is the canonical route (documents→notes rename 2026-08-06); the
    // app-access key stays the internal 'documents'. '/documents' = legacy alias.
    ['/notes', 'documents'],
    ['/documents', 'documents'],
    ['/files', 'files'],
    // /tasks is the canonical route (tickets→tasks rename 2026-08-06); the
    // ACL app key stays the internal 'tickets'. /tickets* = inbound aliases.
    ['/tasks-page', 'tickets'],
    ['/tasks', 'tickets'],
    ['/task', 'tickets'],
    ['/tickets-page', 'tickets'],
    ['/tickets', 'tickets'],
    ['/ticket', 'tickets'],
    ['/reminders', 'reminders'],
    ['/resolutions', 'resolutions'],
    ['/workspace-analytics', 'analytics'],
    ['/analytics', 'analytics'],
    ['/logs', 'analytics'],
    ['/data', 'data'],
    ['/announcements', 'announcements'],
    ['/company', 'company']
  ]

  const p = stripWorkspacePrefix(String(path || '').split('?')[0])
  if (!p) return null
  const hit = APP_ROUTES.find(([base]) => p === base || p.indexOf(base + '/') === 0)
  return hit ? hit[1] : null
}
